import { ErrorMessage, Formik, FormikHelpers } from "formik";
import { memo, useContext } from "react";
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import appConstants, { convertDateToApiFormat } from "../../../app-constants";
import ScrollToFirstError from "../../../common-components/ScrollToFirstError";
import { CorporateGroupTravelContext } from "../../CorporateGroupTravel";
import { formatPhoneNumber } from "../../config/cgConstants";
import { ICGOrderForm, ICorporateGroupTravelContext } from "../../config/interfaces";
import CGQuoteApi from "../api/CGQuoteApi";
import BackArrowIcon from "../icons/BackArrowIcon";
import CGTTripSummary from "../page/CGTripSummary";
import ProgressBar from "../page/ProgressBar";

const validationSchema = yup.object().shape({
  companyName: yup.string().required(),
  contactPhone: yup
    .string()
    .required()
    .test("phoneValidation", "phone must be 10 digits", (phone: string) => {
      return phone.length >= 14;
    }),
  contactEmail: yup
    .string()
    .email("Invalid email address")
    .trim()
    .matches(appConstants.EMAIL_REGEX, "Invalid email address")
    .required(),
  contactFirstName: yup.string().required(),
  contactLastName: yup.string().required(),
  companyAddress: yup.string().required(),
  companyAddress2: yup.string().notRequired(),
  city: yup.string().required(),
  state: yup.string().required(),
  zip: yup.string().required(),
});

const CGOrderForm = () => {
  const { quoteData, next, previous, quoteResult, hasError, setHasError, setOrder } = useContext(
    CorporateGroupTravelContext
  ) as ICorporateGroupTravelContext;

  const initialValues = {
    companyName: "",
    contactPhone: "",
    contactEmail: "",
    contactFirstName: "",
    contactLastName: "",
    companyAddress: "",
    companyAddress2: "",
    city: "",
    state: quoteData?.companyLocation?.code,
    zip: "",
  };

  const handleFormSubmit = async (values: ICGOrderForm, { setSubmitting }: FormikHelpers<ICGOrderForm>) => {
    try {
      setHasError(null);
      setSubmitting(true);
      const createOrderApiData = {
        quote_id: quoteResult?.id,
        currency_id: quoteData?.currency,
        product_id: String(quoteData?.productId),
        start_at: convertDateToApiFormat(quoteData?.startDate),
        end_at: convertDateToApiFormat(quoteData?.endDate),
        details: [
          {
            type: "package",
            product_package_id: "base_package",
          },
          {
            type: "group",
            detail: "travelers",
            amount: quoteData?.numberOfTravellers,
            product_package_id: "base_package",
          },
        ],
        applicant: {
          company_name: values?.companyName,
          first_name: values?.contactFirstName,
          last_name: values?.contactLastName,
          email: values?.contactEmail,
          contact_number: values?.contactPhone,
          address: values?.companyAddress + values.companyAddress2,
          city: values?.city,
          post_code: values?.zip,
          country_code: quoteData?.hostCountry,
          state_code: values?.state,
        },
        destinations: quoteData?.destinationCountries?.map((destination) => ({
          country_code: destination.code,
        })),
        affiliate: {
          id: quoteData?.affiliateId,
        },
      };
      const createOrderResponse = await CGQuoteApi?.createOrder(createOrderApiData);
      if (createOrderResponse.errors) {
        throw createOrderResponse;
      }
      setOrder(createOrderResponse.data);
      next();
    } catch (error: any) {
      setSubmitting(false);
      setHasError(error.message);
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
      {({
        values,
        errors,
        touched,
        handleBlur,
        isValid,
        submitCount,
        setFieldValue,
        isSubmitting,
        handleSubmit,
        handleChange,
        validateForm,
      }: any) => (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await validateForm();
            handleSubmit();
          }}
        >
          <ProgressBar />
          <CGTTripSummary />
          <div className={`${appStyles.CGTBasicForm} ${appStyles.CGTOrderForm}`}>
            <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

            <div className={`${appStyles.CGTBasicFormBlockTitle}`}>
              <h3 className={`${appStyles.CGTBasicFormTitle}`}>Company Information</h3>
            </div>

            {/*Company Name */}
            <div className={appStyles.CGTBasicFormFieldRow}>
              <div className={appStyles.CGTBasicFormFieldCol}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.companyName && errors?.companyName ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>Company Name:</label>
                  <input
                    type={"text"}
                    name={"companyName"}
                    id={"companyName"}
                    value={values.companyName}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"companyName"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>
            </div>
            {/* phone email*/}
            <div className={appStyles.CGTBasicFormFieldRow}>
              <div className={appStyles.CGTBasicFormFieldMD6}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.contactPhone && errors?.contactPhone ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>Contact Phone:</label>

                  <input
                    type={"text"}
                    name={"contactPhone"}
                    id={"contactPhone"}
                    value={values.contactPhone}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={(event: any) => {
                      setFieldValue("contactPhone", formatPhoneNumber(event.target.value));
                    }}
                    maxLength={14}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"contactPhone"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>
              <div className={appStyles.CGTBasicFormFieldMD6}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.contactEmail && errors?.contactEmail ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>Contact Email:</label>

                  <input
                    type={"text"}
                    name={"contactEmail"}
                    id={"contactEmail"}
                    value={values.contactEmail}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"contactEmail"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>
            </div>
            {/* First and last  */}
            <div className={appStyles.CGTBasicFormFieldRow}>
              <div className={appStyles.CGTBasicFormFieldMD6}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.contactFirstName && errors?.contactFirstName ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>Contact First Name:</label>
                  <input
                    type={"text"}
                    name={"contactFirstName"}
                    id={"contactFirstName"}
                    value={values?.contactFirstName}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"contactFirstName"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>

              <div className={appStyles.CGTBasicFormFieldMD6}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.contactLastName && errors?.contactLastName ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>Contact Last Name:</label>
                  <input
                    type={"text"}
                    name={"contactLastName"}
                    id={"contactLastName"}
                    value={values?.contactLastName}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"contactLastName"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>
            </div>

            {/* Address  */}
            <div className={appStyles.CGTBasicFormFieldRow}>
              <div className={appStyles.CGTBasicFormFieldCol}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.companyAddress && errors?.companyAddress ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>Company Address:</label>
                  <input
                    type={"text"}
                    name={"companyAddress"}
                    id={"companyAddress"}
                    value={values?.companyAddress}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"companyAddress"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>
            </div>
            {/* Address optional */}
            <div className={appStyles.CGTBasicFormFieldRow}>
              <div className={appStyles.CGTBasicFormFieldCol}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.companyAddress2 && errors?.companyAddress2 ? appStyles.hasError : ""
                  }`}
                >
                  <input
                    type={"text"}
                    name={"companyAddress2"}
                    id={"companyAddress2"}
                    value={values?.companyAddress2}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"companyAddress2"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>
            </div>
            {/* city state zipcode  */}

            <div className={appStyles.CGTBasicFormFieldRow}>
              <div className={appStyles.CGTBasicFormFieldMD4}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.city && errors?.city ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>City:</label>
                  <input
                    type={"text"}
                    name={"city"}
                    id={"city"}
                    value={values.city}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"city"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>

              <div className={appStyles.CGTBasicFormFieldMD4}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${
                    touched?.state && errors?.state ? appStyles.hasError : ""
                  }`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>State:</label>
                  <input
                    type={"text"}
                    name={"state"}
                    id={"state"}
                    value={quoteData?.companyLocation?.code}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    disabled={true}
                  />
                  <ErrorMessage name={"state"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>

              <div className={appStyles.CGTBasicFormFieldMD4}>
                <div
                  className={`${appStyles.CGTOrderFormField} ${touched?.zip && errors?.zip ? appStyles.hasError : ""}`}
                >
                  <label className={appStyles.CGTBasicFormLabel}>Zip Code:</label>
                  <input
                    type={"text"}
                    name={"zip"}
                    id={"zip"}
                    value={values.zip}
                    className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage name={"zip"}>
                    {(msg) => <div className={appStyles.formError}>This field is required</div>}
                  </ErrorMessage>
                </div>
              </div>
            </div>
          </div>
          <div className={appStyles.CGTBasicFormPreFooter}>
            <button className={`${appStyles.CGTBasicFormBackbtn} `} onClick={previous}>
              <BackArrowIcon />
              BACK
            </button>

            <button className={`${appStyles.CGTBasicFormBtnPrimary}`} type={"submit"}>
              {!isSubmitting ? "Proceed To Checkout" : <span className={appStyles.SmallSpinner} />}
            </button>
          </div>
          {hasError && (
            <div className={appStyles.formRow}>
              <div className={appStyles.battlefaceServerError}>{hasError}</div>
            </div>
          )}
        </form>
      )}
    </Formik>
  );
};

export default memo(CGOrderForm);
